import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';

import { Moment } from 'moment';
import React, { useState } from 'react';
import { DateRangePicker as ReactDateRangePicker } from 'react-dates';
import { ICON_AFTER_POSITION } from 'react-dates/constants';
import styled from 'styled-components';

import { colors, Theme } from '../../../core';
import Icon from '../Icon';
import Label from '../Label';
import Text from '../Text';

export interface DatePickerProps {
  className?: string;
  id?: string;
  disabled?: boolean;
  label?: string;
  animatedLabel?: string;
  name?: string;
  onBlur?: () => void;
  onChange: (arg: { startDate: Moment | null; endDate: Moment | null }) => void;
  onMonthChange?: () => void;
  width?: string;
  value: {
    startDate: Moment | null;
    endDate: Moment | null;
  };
  yearPicker?: any;
  numberOfMonths?: number;
  renderMonthContents?: any;
  futureYears?: number;
  focusedInput?: 'startDate' | 'endDate' | null;
  showPastDays?: boolean;
  showRequired?: boolean;
  errorMessage?: string;
  variant?: 'primary' | 'secondary';
}

export interface StyledDatePickerProps {
  className?: string;
  name?: string;
  onBlur?: () => void;
  width?: string;
  yearPicker?: any;
  isError?: boolean;
  variant?: 'primary' | 'secondary';
}

const PrevIcon = styled(Icon).attrs(({ theme }) => ({
  type: 'caret-left',
  color: theme.datePickerIconColor || colors.primary,
}))`
  position: absolute;
  top: 24px;
  left: 22px;
  cursor: pointer;
  &:hover {
    path {
      fill: ${({ theme }) => theme.datePickerIconColorHover || colors.primaryHover};
    }
  }
`;

const NextIcon = styled(Icon).attrs(({ theme }) => ({
  type: 'caret-right',
  color: theme.datePickerIconColor || colors.primary,
}))`
  position: absolute;
  top: 24px;
  right: 22px;
  cursor: pointer;
  &:hover {
    path {
      fill: ${({ theme }) => theme.datePickerIconColorHover || colors.primaryHover};
    }
  }
`;

const StyledDateRangePicker = styled.div<StyledDatePickerProps>`
  position: relative;

  .DateRangePicker {
    display: block;
    width: ${({ width }) => width || '100%'};
  }

  .DateRangePicker_picker {
    margin-top: ${({ theme }) => theme.datePickerTop || 0};
  }

  .DateRangePickerInput {
    display: flex;
    overflow: hidden;
    background-color: ${({ theme }) => theme.datePickerBackgroundColor || colors.white};

    &:hover {
      box-shadow: ${({ theme }) =>
        theme.datePickerHoverShadow || '0 2px 4px 0 rgba(217, 217, 217, 0.5)'};
    }

    &__withBorder {
      border-color: ${({ isError, theme, variant }) =>
        isError
          ? theme.errorColor
          : (variant === 'primary'
              ? theme.textAreaInputBorderColor
              : theme.textAreaInputSecondaryBorderColor) || colors.smoke};
      border-radius: ${({ theme }) => theme.borderRadius || '3px'};

      ${({ theme, variant }) =>
        variant === 'secondary' &&
        theme.datePickerShowOnlyBottomBorder &&
        `
          border-top: 0;
          border-left: 0;
          border-right: 0;
          border-radius: 0;
      `}
    }

    &__disabled {
      background-color: ${({ theme }) =>
        !theme.datePickerShowOnlyBottomBorder
          ? theme.textInputDisabledBackgroundColor || colors.pearl
          : 'transparent'};
      border-color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};

      &:hover {
        box-shadow: none;
      }

      .DateRangePickerInput_arrow_svg path {
        fill: ${({ theme }) => theme.dateRangePickerDisabledIconColor || colors.smoke};
      }

      .DateRangePickerInput_calendarIcon {
        cursor: default;
      }
    }
  }

  .DateRangePickerInput_arrow {
    display: inline-flex;
    align-items: center;

    &_svg {
      path {
        fill: ${({ theme }) => theme.dateRangePickerIconColor || colors.ash};
      }
    }
  }

  .DateRangePickerInput_calendarIcon {
    line-height: 1;
    margin: 0;
    padding: ${({ theme }) => theme.datePickerCalendarIconPadding || '6px 10px'};

    svg {
      cursor: pointer;
    }
  }

  .DateInput {
    flex: 1;

    color: ${({ theme }) => theme.datePickerColor || colors.ash};
    background-color: transparent;

    &__disabled {
      background-color: ${({ theme }) =>
        !theme.datePickerShowOnlyBottomBorder
          ? theme.textInputDisabledBackgroundColor || colors.pearl
          : 'transparent'};
      border-color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};
    }
  }

  .DateInput_input {
    height: ${({ theme }) => theme.datePickerInputHeight || '34px'};
    font-family: ${({ theme }) => theme.fontFamily || `Quicksand, sans-serif`};
    font-size: ${({ theme }) => theme.fontSize || '14px'};
    font-weight: ${({ theme }) => theme.fontWeight || 600};
    line-height: 20px;
    padding: ${({ theme }) => theme.datePickerPadding || '7px 10px 5px'};
    color: ${({ theme }) => theme.datePickerColor || colors.ash};
    background-color: transparent;

    &::placeholder {
      color: ${({ theme }) => theme.datePickerPlaceholderColor || colors.smoke};
    }

    &__focused {
      background-color: transparent;
      border-bottom-color: ${({ theme }) => theme.datePickerFocusedBorderColor || colors.primary};
    }

    &__disabled {
      background-color: ${({ theme }) =>
        !theme.datePickerShowOnlyBottomBorder
          ? theme.textInputDisabledBackgroundColor || colors.pearl
          : 'transparent'};
      color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};
      font-style: normal;
      -webkit-text-fill-color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};
      opacity: 1;
    }
  }

  .CalendarDay__blocked_out_of_range {
    border: 0;
    color: ${({ theme }) => theme.textInputDisabledColor || colors.primary};
  }

  .CalendarDay__selected_span {
    background-color: ${({ theme }) =>
      theme.dateRangePickerSpanActiveBackgroundColor || colors.ash};
    border-color: ${({ theme }) => theme.dateRangePickerSpanActiveBorderColor || colors.smoke};
    color: ${({ theme }) => theme.dateRangePickerSpanActiveColor || colors.smoke};
  }

  .CalendarDay__selected_start,
  .CalendarDay__selected_end {
    background-color: ${({ theme }) => theme.datePickerActiveBackgroundColor || colors.primary};
    border-color: ${({ theme }) => theme.datePickerActiveBorderColor || colors.primary};
    color: ${({ theme }) => theme.datePickerActiveColor || colors.white};
  }

  .CalendarDay:hover {
    background-color: ${({ theme }) => theme.datePickerHoverBackgroundColor || colors.primary};
    border-color: ${({ theme }) => theme.datePickerHoverBorderColor || colors.primary};
    color: ${({ theme }) => theme.datePickerHoverColor || colors.white};
  }

  .CalendarDay__hovered_span {
    background-color: ${({ theme }) => theme.dateRangePickerSpanHoverBackgroundColor || colors.ash};
    border-color: ${({ theme }) => theme.dateRangePickerSpanHoverBorderColor || colors.smoke};
    color: ${({ theme }) => theme.dateRangePickerSpanHoverColor || colors.smoke};
  }
`;

const StyledIcon = styled(Icon).attrs(({ theme }) => ({
  color: theme.datePickerIconColor || colors.primary,
}))``;

const DisabledIcon = styled(Icon).attrs(({ theme }) => ({
  color: theme.datePickerIconDisabledColor || colors.primaryDisabled,
}))`
  cursor: default !important;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const StyledLabel = styled(Label)<{ variant?: 'primary' | 'secondary' }>`
  ${({ theme, variant }) =>
    theme.name !== Theme.theme1 &&
    variant === 'secondary' &&
    `
      font-size: 12px;
      font-weight: 400;
      padding: 0 16px;
      margin: 0;
      position: absolute;
      top: -8px;
      line-height: 1.5;
    `}
`;

const AnimatedLabel = styled(Label)`
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: 2;
  transition: top 300ms ease-in-out, font-size 300ms ease-in-out, opacity 300ms ease-in-out;
  color: ${({ theme }) => theme.textInputColor || colors.ash};
  z-index: ${({ theme }) => theme.name !== Theme.theme1 && -1};
`;

const ErrorMessage = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.errorColor};
  padding: ${({ theme }) => theme.inputErrorMessagePadding || '0'};
  line-height: 1.5;
`;

/**
 * DateRangePicker style wrapper around react-dates library. The implementer of this component must
 * manage start and end date states internally using onChange to update it.
 */
const DateRangePicker = ({
  className,
  disabled,
  label,
  name,
  onBlur,
  onChange,
  width,
  value,
  showPastDays = false,
  animatedLabel,
  showRequired,
  errorMessage,
  variant = 'primary',
  ...rest
}: DatePickerProps) => {
  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate' | null>(null);

  return (
    <StyledDateRangePicker
      className={className}
      name={name}
      onBlur={onBlur}
      width={width}
      isError={!!errorMessage}
      variant={variant}
    >
      {label && (
        <StyledLabel variant={variant}>
          {label}
          {showRequired && '*'}
        </StyledLabel>
      )}
      <InputWrapper>
        <ReactDateRangePicker
          {...rest}
          customInputIcon={
            disabled ? <DisabledIcon type="input-calendar" /> : <StyledIcon type="input-calendar" />
          }
          disabled={disabled}
          endDate={value.endDate}
          endDateId="end-date"
          focusedInput={focusedInput}
          hideKeyboardShortcutsPanel={true}
          inputIconPosition={ICON_AFTER_POSITION}
          minimumNights={0}
          navNext={<NextIcon />}
          navPrev={<PrevIcon />}
          onDatesChange={onChange}
          onFocusChange={(focusedInput: any) => setFocusedInput(focusedInput)}
          startDate={value.startDate}
          startDateId="start-date"
          verticalSpacing={0}
          isOutsideRange={showPastDays ? () => false : undefined}
        />
        {animatedLabel && (
          <AnimatedLabel>
            {animatedLabel}
            {showRequired && '*'}
          </AnimatedLabel>
        )}
      </InputWrapper>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </StyledDateRangePicker>
  );
};

export default DateRangePicker;
